import React from 'react';
import { BlockMath, InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import './MathSolution.css';

/**
 * MathSolution - Component for displaying mathematical solutions with LaTeX rendering
 * Similar to WebSearchResults but for math formulas and step-by-step solutions
 */
const MathSolution = ({ mathData }) => {
  // State for Socratic interaction (must be called before any early returns)
  const [socraticLevel, setSocraticLevel] = React.useState(0);
  const [showHint, setShowHint] = React.useState(false);
  const [studentResponse, setStudentResponse] = React.useState('');
  const [showCelebration, setShowCelebration] = React.useState(false);

  if (!mathData) {
    return (
      <div className="math-solution">
        <div className="math-header">
          <h3>No Math Solution</h3>
          <p>No solution data available.</p>
        </div>
      </div>
    );
  }

  const { 
    type,
    problem, 
    solution, 
    latex_output, 
    steps,  // Step-by-step data from MCP server
    // Socratic mode fields
    mode,
    initial_question,
    questions,
    celebration
  } = mathData;

  // Helper function to render LaTeX using react-katex with proper spacing
  const renderLatex = (latexString) => {
    if (!latexString) return null;
    
    // Check for separated problem and solution (using ||| separator)
    if (latexString.includes('|||')) {
      const parts = latexString.split('|||');
      return (
        <div className="math-sections">
          {parts.map((part, index) => (
            <div key={index} className="math-section">
              <BlockMath math={part} />
            </div>
          ))}
        </div>
      );
    }
    
    // Check if it's display mode (surrounded by $$) or inline mode
    if (latexString.includes('$$')) {
      // Display mode - remove $$ markers and render as block
      const latexContent = latexString.replace(/\$\$/g, '');
      return (
        <div className="latex-display">
          <BlockMath math={latexContent} />
        </div>
      );
    } else if (latexString.includes('$')) {
      // Inline mode - remove $ markers
      const latexContent = latexString.replace(/\$/g, '');
      return (
        <div className="latex-inline">
          <InlineMath math={latexContent} />
        </div>
      );
    }
    
    // Fallback to plain text if no LaTeX markers
    return <p className="math-text">{latexString}</p>;
  };

  // Socratic interaction handlers
  const handleStudentResponse = () => {
    if (studentResponse.trim()) {
      // Simple response validation - in real app, could use LLM to evaluate
      const isGoodResponse = studentResponse.length > 2;
      
      if (isGoodResponse) {
        if (socraticLevel < (questions?.length || 0) - 1) {
          setSocraticLevel(socraticLevel + 1);
          setStudentResponse('');
          setShowHint(false);
        } else {
          setShowCelebration(true);
        }
      } else {
        setShowHint(true);
      }
    }
  };

  const handleHintRequest = () => {
    setShowHint(true);
  };

  const getCurrentQuestion = () => {
    if (!questions || questions.length === 0) return null;
    return questions[socraticLevel] || questions[0];
  };

  // Determine if we're in Socratic mode
  const isSocraticMode = type === 'socratic_math' || mode === 'questioning';
  const currentQuestion = getCurrentQuestion();

  // Render based on mode
  return isSocraticMode ? (
    // Socratic Math Mode
    <div className="math-solution-clean socratic-mode">
      {/* Problem display */}
      <div className="math-problem-display">
        <div className="problem-equation">
          <BlockMath math={problem} />
        </div>
      </div>

      {/* Socratic questioning interface */}
      <div className="socratic-interface">
        {!showCelebration ? (
          <>
            {/* Current question */}
            <div className="socratic-question">
              <h3>{socraticLevel === 0 ? initial_question : currentQuestion?.question}</h3>
            </div>

            {/* Student input */}
            <div className="student-input-section">
              <textarea
                value={studentResponse}
                onChange={(e) => setStudentResponse(e.target.value)}
                placeholder="Type your thoughts here..."
                className="student-response-input"
                rows="3"
              />
              <div className="socratic-buttons">
                <button onClick={handleStudentResponse} className="submit-response">
                  Submit Response
                </button>
                <button onClick={handleHintRequest} className="hint-request">
                  Need a Hint?
                </button>
              </div>
            </div>

            {/* Hint display */}
            {showHint && currentQuestion?.hint && (
              <div className="hint-display">
                <strong>Hint:</strong> {currentQuestion.hint}
              </div>
            )}

            {/* Progress indicator */}
            <div className="progress-indicator">
              Question {socraticLevel + 1} of {questions?.length || 1}
            </div>
          </>
        ) : (
          /* Celebration screen */
          <div className="celebration-screen">
            <h2>🎉 Excellent Work!</h2>
            <p>{celebration}</p>
            <p>You discovered the solution through your own reasoning!</p>
          </div>
        )}
      </div>
    </div>
  ) : (
    // Traditional Math Solution Mode
    <div className="math-solution-clean">
      {/* Clean, minimal math display with white background */}
      <div className="math-display-area">
        {/* Display step-by-step solution from MCP server */}
        {steps && steps.length > 0 ? (
          <div className="math-steps">
            {steps.map((step) => (
              <div key={step.step} className="math-step">
                <div className="step-number">Step {step.step}</div>
                <div className="step-expression">
                  <BlockMath math={step.expression} />
                </div>
                <div className="step-description">{step.description}</div>
              </div>
            ))}
          </div>
        ) : latex_output ? (
          renderLatex(latex_output)
        ) : (
          <div className="fallback-math">
            <div className="math-equation">{problem}</div>
            <div className="math-answer">Solution: {solution}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MathSolution;